// Map from cart contents to the richer model used for editing
// We may be restoring from an old kit that is no longer valid in the current catalog (for reordering old
// kits), so we only overwrite the default state with data we've validated against the catalog.
import { consumerConfigurations, ProbeDetect } from "../../KitDeserializer";
import { findOrganismByName, isPredictedTarget, targetHasMIValidatedEntry } from "../KitToFormStateUtilityFunctions";
import { RnaCishCatalogVariantRefProbeSet, RnaCishLibraryOrganismCustomTargetProbeSet } from "../rna-fish/KitModel";
import { extractNameBeforeParenthesis } from "../rna-fish/RnaFishKitToFormState";
import { RnaCishEditableOrganism, RnaCishEditableTarget } from "./ProbeSetForm";
/**
 * Converts a kit to a consumer form state. If the kit in question does not contain only catalog items such as
 * entries components and scales, then we revert to the default consumer form state settings.
 * @return RnaCishConsumerEditKitForm
 */
export var rnaCishKitToProFormState = function (catalog, kit) {
    var kitConfiguration;
    var organism = RnaCishEditableOrganism.default();
    var target = RnaCishEditableTarget.default(-1);
    var targets = [];
    var enzymeCompatibility;
    var chromogen;
    var kitScale;
    kitConfiguration = consumerConfigurations.find(function (c) { return kit.config === c; });
    if (kitConfiguration == null) {
        kitConfiguration = ProbeDetect;
    }
    if (kit.probeSetComponent !== undefined) {
        var probeSet_1 = kit.probeSetComponent.probeSet;
        // Since a consumer will only be able to purchase catalog probe sets we should only be converting the kit to
        // a consumer form state if it contains a catalog entry ref probe set.
        if (probeSet_1 instanceof RnaCishCatalogVariantRefProbeSet) {
            var customerProvidedOrganism = findOrganismByName(extractNameBeforeParenthesis(probeSet_1.customerOrganismName), catalog);
            if (customerProvidedOrganism != null) {
                var customerProvidedTarget = customerProvidedOrganism.probeSetFamilies.find(function (psf) {
                    return psf.geneSymbol.trim().toLowerCase()
                        === extractNameBeforeParenthesis(probeSet_1.customerTargetName).trim().toLowerCase();
                });
                if (customerProvidedTarget != null) {
                    var customerProvidedVariant = customerProvidedTarget.variants.find(function (v) {
                        var accession = v.accession ? v.accession.transcriptAccession : "";
                        return v.targetMrnaSeq === probeSet_1.customerSequence
                            && v.probeSetSize === probeSet_1.customerProbeSetSize
                            && accession.trim().toLowerCase() === probeSet_1.customerAccessionNum.trim().toLowerCase();
                    });
                    if (customerProvidedVariant != null) {
                        organism = RnaCishEditableOrganism.fromLibOrg(customerProvidedOrganism);
                        target = RnaCishEditableTarget.fromLibTarget(customerProvidedVariant);
                        targets = [
                            {
                                id: customerProvidedVariant.probeSetFamily.id,
                                geneSymbol: customerProvidedVariant.probeSetFamily.geneSymbol,
                                description: customerProvidedVariant.probeSetFamily.description,
                                synonyms: customerProvidedVariant.probeSetFamily.synonyms
                            }
                        ];
                    }
                    else {
                        organism = RnaCishEditableOrganism.fromLibOrg(customerProvidedOrganism);
                        target = new RnaCishEditableTarget(customerProvidedTarget.geneSymbol, customerProvidedTarget.description, probeSet_1.customerAccessionNum, probeSet_1.customerSequence, probeSet_1.customerProbeSetSize, customerProvidedTarget.id, targetHasMIValidatedEntry(customerProvidedTarget), isPredictedTarget(customerProvidedTarget), undefined);
                        targets = [
                            {
                                id: customerProvidedTarget.id,
                                geneSymbol: customerProvidedTarget.geneSymbol,
                                description: customerProvidedTarget.description,
                                synonyms: customerProvidedTarget.synonyms
                            }
                        ];
                    }
                }
                else {
                    organism = RnaCishEditableOrganism.fromLibOrg(customerProvidedOrganism);
                    target = new RnaCishEditableTarget(probeSet_1.customerTargetName, "", probeSet_1.customerAccessionNum, probeSet_1.customerSequence, probeSet_1.customerProbeSetSize, -1, false, false, undefined);
                }
            }
            else {
                organism = new RnaCishEditableOrganism(probeSet_1.customerOrganismName, "", -1);
                target = new RnaCishEditableTarget(probeSet_1.customerTargetName, "", probeSet_1.customerAccessionNum, probeSet_1.customerSequence, probeSet_1.customerProbeSetSize, -1, false, false, undefined);
            }
            var variant_1 = probeSet_1.variant;
            var family_1 = probeSet_1.probeSetFamily;
            var simpleOrganism_1 = probeSet_1.organism;
            // Look for the entry. Since the consumer kits that a normal consumer will see will only contain catalog
            // probe sets we either match the entry id with one of the catalog entry ids, or we find the equivalent entry
            // if a new batch was uploaded based off amplifier, variant, geneSymbol, probe set size and
            // organism
            catalog.probeSet.catalog.organisms.forEach(function (o) {
                o.probeSetFamilies.forEach(function (psf) {
                    psf.variants.forEach(function (v) {
                        if (v.id === variant_1.id
                            || (v.probeSetSize === variant_1.probeSetSize
                                && v.probeSetFamily.geneSymbol === family_1.geneSymbol
                                && v.organism.name === simpleOrganism_1.name
                                && v.name === variant_1.name)) {
                            target = target.withProbeDesign(v.id);
                        }
                    });
                });
            });
        }
        else if (probeSet_1 instanceof RnaCishLibraryOrganismCustomTargetProbeSet) {
            catalog.probeSet.catalog.organisms.forEach(function (o) {
                if (o.id === probeSet_1.organism.id) {
                    organism = RnaCishEditableOrganism.fromLibOrg(o);
                    target = new RnaCishEditableTarget(probeSet_1.target.name, "", probeSet_1.target.accession, probeSet_1.target.targetMrnaSeq, probeSet_1.probeSetSize, -1, false, false, undefined);
                }
            });
        }
        else {
            organism = new RnaCishEditableOrganism(probeSet_1.organism.name, "", -1);
            target = new RnaCishEditableTarget(probeSet_1.target.name, "", probeSet_1.target.accession, probeSet_1.target.targetMrnaSeq, probeSet_1.probeSetSize, -1, false, false, undefined);
        }
    }
    if (kit.amplifierComponent !== undefined) {
        var ampComponent_1 = kit.amplifierComponent;
        enzymeCompatibility = catalog.cishAmplifier.amplifierConfig
            .filter(function (p) { return p.protocolType.id === kit.protocolType.id; })
            .find(function (u) {
            var _a, _b, _c, _d;
            return (u.amplifier.id === ampComponent_1.hcrAmplifier.id &&
                u.hapten.id === ampComponent_1.hapten.id &&
                u.antiHapten1.id === ampComponent_1.antiHapten1.id &&
                u.tyramideHapten.id === ampComponent_1.tyramideHapten.id &&
                ((_a = u.antiHapten2Ap) === null || _a === void 0 ? void 0 : _a.id) === ((_b = ampComponent_1.antiHapten2Ap) === null || _b === void 0 ? void 0 : _b.id) &&
                ((_c = u.antiHapten2Hrp) === null || _c === void 0 ? void 0 : _c.id) === ((_d = ampComponent_1.antiHapten2Hrp) === null || _d === void 0 ? void 0 : _d.id));
        });
    }
    if (kit.chromogenComponent !== undefined) {
        chromogen = catalog.cishChromogens.chromogens
            .find(function (c) {
            var _a, _b;
            return c.id === ((_b = (_a = kit.chromogenComponent) === null || _a === void 0 ? void 0 : _a.chromogen) === null || _b === void 0 ? void 0 : _b.id);
        });
    }
    if (kit.probeSetComponent !== undefined) {
        var psc_1 = kit.probeSetComponent;
        var scale = catalog.probeSet.catalog.cishScale.find(function (s) { return s === psc_1.scale.slides; });
        if (scale === undefined) {
            kitScale = catalog.probeSet.catalog.cishDefaultScale;
        }
        else {
            kitScale = psc_1.scale.slides;
        }
    }
    else if (kit.amplifierComponent !== undefined) {
        var amp_1 = kit.amplifierComponent;
        var scale = catalog.cishAmplifier.amplifierScales.find(function (s) { return s === amp_1.amplifierScale.slides; });
        if (scale === undefined) {
            kitScale = catalog.cishAmplifier.defaultAmplifierScale;
        }
        else {
            kitScale = amp_1.amplifierScale.slides;
        }
    }
    else if (kit.chromogenComponent !== undefined) {
        var scale = catalog.cishChromogens.proChromogenScales.find(function (s) { var _a; return s === ((_a = kit.chromogenComponent) === null || _a === void 0 ? void 0 : _a.chromogenScale.slides); });
        if (scale === undefined) {
            kitScale = catalog.cishChromogens.proDefaultChromogenScale;
        }
        else {
            kitScale = kit.chromogenComponent.chromogenScale.slides;
        }
    }
    else {
        var buf_1 = kit.bufferComponent;
        var scale = catalog.cishBuffer.proBufferScales.find(function (s) { return s === buf_1.amplificationBufferScale.slides; });
        if (scale === undefined) {
            kitScale = catalog.cishBuffer.proDefaultBufferScale;
        }
        else {
            kitScale = buf_1.amplificationBufferScale.slides;
        }
    }
    return {
        protocolType: kit.protocolType,
        kitConfiguration: kitConfiguration,
        scale: kitScale,
        organism: organism ? organism : RnaCishEditableOrganism.default(),
        target: target ? target : RnaCishEditableTarget.default(-1),
        enzymeCompatibility: enzymeCompatibility,
        probeSetFamilies: targets,
        chromogen: chromogen,
        errors: [],
        searchQuery: '',
        searchLoading: false,
        enableCustomTarget: true,
        comment: "",
        priceLoading: false,
        price: undefined
    };
};
//# sourceMappingURL=RnaCishKitToProFormState.js.map


//////////////////
// WEBPACK FOOTER
// ../common/build/dist/kit/rna-cish/RnaCishKitToProFormState.js
// module id = 544
// module chunks = 0